import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import HospitalFormModal from "../components/HospitalFormModal";
import { HOSPITAL_LIST_PATH, HOSPITAL_LIST_URL_KEY, resolveListBackTarget } from "../utils/subscriberHelpers";
import {
  getHospitalDetail,
  getHospitalContacts,
  createHospitalContact,
  editHospitalContact,
  getHospitalDocuments,
  uploadHospitalDocument,
  getHospitalBank,
  saveHospitalBank,
  editHospitalBank,
  getContractSigningAuthority,
  saveContractSigningAuthority,
  getTreatmentPackages,
  createTreatmentPackage,
  updateTreatmentPackage,
  addRoomPriceToPackage,
  editPackageRoomPrice,
  createHospitalRoom,
  editHospitalRoom,
  addCustomRoom,
  getHospitalRooms,
  getFinancialConfig,
  saveFinancialConfig,
  editFinancialConfig,
  editHospitalDocument,
} from "../services/hospitalService";

const getCurrentRole = () => {
  const authRole = localStorage.getItem("authRole");
  const roleMapping = {
    wallet_superadmin: "SuperAdmin",
    wallet_finance: "Finance",
    wallet_ops: "Ops",
    wallet_support: "Support",
  };
  return roleMapping[authRole] || null;
};

const SPECIALTY_LABEL = {
  cardiology_cardiothoracic_surgery: "Cardiology & Cardiothoracic Surgery",
  neurology_neurosurgery: "Neurology & Neurosurgery",
  orthopedics_joint_replacement: "Orthopedics & Joint Replacement",
  gastroenterology_hepatology: "Gastroenterology & Hepatology",
  oncology_hemato_oncology: "Oncology & Hemato-Oncology",
  gynecology_obstetrics: "Gynecology & Obstetrics",
  pediatrics_neonatology: "Pediatrics & Neonatology",
  nephrology_urology: "Nephrology & Urology",
  pulmonology_critical_care: "Pulmonology & Critical Care",
  ent_head_neck_surgery: "ENT & Head-Neck Surgery",
  ophthalmology: "Ophthalmology",
  dermatology_cosmetology: "Dermatology & Cosmetology",
  endocrinology_diabetology: "Endocrinology & Diabetology",
  general_surgery_laparoscopy: "General Surgery & Laparoscopy",
  plastic_reconstructive_surgery: "Plastic & Reconstructive Surgery",
  mental_health_psychiatry: "Mental Health & Psychiatry",
  emergency_medicine: "Emergency Medicine",
  general_medicine_family_care: "General Medicine & Family Care",
};

const AMENITY_LABEL = {
  atm: "ATM",
  drinking_water: "Drinking Water",
  ac: "AC",
  food_court: "Food Court",
  washrooms: "Washrooms",
  lounge: "Lounge",
  prayer_rooms: "Prayer Rooms",
  parking: "Parking",
  wifi: "WiFi",
  cafeteria: "Cafeteria",
};

const DIAGNOSTIC_SERVICES_LABEL = {
  x_ray: "X-Ray",
  ct_scan: "CT Scan",
  mri: "MRI",
  pet_scan: "PET Scan",
  echocardiography: "Echocardiography",
  ultrasound: "Ultrasound",
  pathology_labs: "Pathology Labs",
  pharmacy: "Pharmacy",
  orthotics_prosthetics: "Orthotics & Prosthetics",
};

const CONTACT_TYPES = [
  { label: "Front-Desk", value: "front_desk" },
  { label: "Reception", value: "reception" },
  { label: "Admin", value: "admin" },
  { label: "Operations", value: "operations" },
  { label: "Emergency", value: "emergency" },
  { label: "Owner", value: "owner" },
  { label: "TPA Head", value: "tpa_head" },
  { label: "GM", value: "gm" },
];

const CONTACT_TYPE_LABEL = Object.fromEntries(
  CONTACT_TYPES.map(({ label, value }) => [value, label])
);

const EMPTY_FORM = {
  name: "",
  contact_type: "",
  mobile: "",
  email: "",
  is_primary: false,
};

const Info = ({ label, value }) => (
  <div>
    <div className="text-gray-500 text-xs">{label}</div>
    <div className="font-medium text-gray-900">{value ?? "N/A"}</div>
  </div>
);

const Badge = ({ label, active }) => (
  <span
    className={`px-3 py-1 text-xs rounded-md font-medium ${active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
      }`}
  >
    {label}
  </span>
);

// ─── Contact Form Modal ───────────────────────────────────────────────────────

const ContactFormModal = ({ onClose, onSave, existingMobiles }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.contact_type) e.contact_type = "Contact type is required.";
    if (!form.mobile) {
      e.mobile = "Phone number is required.";
    } else if (!/^\d{10}$/.test(form.mobile)) {
      e.mobile = "Phone must be exactly 10 digits.";
    } else if (existingMobiles.includes(form.mobile)) {
      e.mobile = "This phone number is already registered for this hospital.";
    }
    if (!form.email) {
      e.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = "Enter a valid email address.";
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) {
      setErrors(e2);
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const field = (key, value) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-semibold text-gray-900">Add Contact</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => field("name", e.target.value)}
              placeholder="Contact person's name"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Contact Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Contact Type <span className="text-red-500">*</span>
            </label>
            <select
              value={form.contact_type}
              onChange={(e) => field("contact_type", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select type</option>
              {CONTACT_TYPES.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            {errors.contact_type && (
              <p className="text-red-500 text-xs mt-1">{errors.contact_type}</p>
            )}
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              maxLength={10}
              value={form.mobile}
              onChange={(e) =>
                field("mobile", e.target.value.replace(/\D/g, ""))
              }
              placeholder="10-digit number"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.mobile && (
              <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => field("email", e.target.value)}
              placeholder="example@domain.com"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Primary Contact */}
          <div className="flex items-center gap-2">
            <input
              id="is_primary"
              type="checkbox"
              checked={form.is_primary}
              onChange={(e) => field("is_primary", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="is_primary"
              className="text-sm text-gray-700 cursor-pointer"
            >
              Mark as Primary Contact
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Contact"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Contact Card ─────────────────────────────────────────────────────────────

const EditContactFormModal = ({ contact, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: contact.name || "",
    contact_type: contact.contact_type || "",
    mobile: contact.mobile || "",
    email: contact.email || "",
    is_primary: !!contact.is_primary,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.contact_type) e.contact_type = "Contact type is required.";
    if (!form.mobile) {
      e.mobile = "Phone number is required.";
    } else if (!/^\d{10}$/.test(form.mobile)) {
      e.mobile = "Phone must be exactly 10 digits.";
    }
    if (!form.email) {
      e.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = "Enter a valid email address.";
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  const field = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const cls = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-semibold text-gray-900">Edit Contact</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
            <input type="text" value={form.name} onChange={(e) => field("name", e.target.value)} placeholder="Contact person's name" className={cls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Contact Type <span className="text-red-500">*</span></label>
            <select value={form.contact_type} onChange={(e) => field("contact_type", e.target.value)} className={cls}>
              <option value="">Select type</option>
              {CONTACT_TYPES.map(({ label, value }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {errors.contact_type && <p className="text-red-500 text-xs mt-1">{errors.contact_type}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
            <input type="tel" maxLength={10} value={form.mobile} onChange={(e) => field("mobile", e.target.value.replace(/\D/g, ""))} placeholder="10-digit number" className={cls} />
            {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
            <input type="email" value={form.email} onChange={(e) => field("email", e.target.value)} placeholder="example@domain.com" className={cls} />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>
          <div className="flex items-center gap-2">
            <input id="edit_is_primary" type="checkbox" checked={form.is_primary} onChange={(e) => field("is_primary", e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            <label htmlFor="edit_is_primary" className="text-sm text-gray-700 cursor-pointer">Mark as Primary Contact</label>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ContactCard = ({ contact, onEdit }) => {
  const typeColors = {
    front_desk: "bg-blue-100 text-blue-700",
    admin: "bg-purple-100 text-purple-700",
    emergency: "bg-red-100 text-red-700",
    owner: "bg-amber-100 text-amber-700",
    gm: "bg-teal-100 text-teal-700",
  };
  const colorClass =
    typeColors[contact.contact_type] || "bg-gray-100 text-gray-700";

  return (
    <div className="group relative border border-gray-200 rounded-md p-4 pb-8 bg-white shadow-sm overflow-hidden">
      {contact.is_primary && (
        <span className="absolute top-3 right-3 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
          Primary
        </span>
      )}
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${colorClass}`}>
          {CONTACT_TYPE_LABEL[contact.contact_type] ?? contact.contact_type}
        </span>
      </div>
      {contact.name && (
        <div className="text-sm font-medium text-gray-900 mb-1">{contact.name}</div>
      )}
      <div className="space-y-1 text-sm">
        <div className="flex items-center gap-2 text-gray-700">
          <span className="text-gray-400">📞</span>
          {contact.mobile}
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <span className="text-gray-400">✉️</span>
          {contact.email}
        </div>
      </div>
      <div className="absolute bottom-2.5 inset-x-3 flex justify-end translate-y-10 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 ease-out">
        <button
          onClick={onEdit}
          className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 border border-gray-200 rounded-full bg-white shadow-sm hover:bg-gray-50"
        >
          Edit
        </button>
      </div>
    </div>
  );
};

// ─── Contacts Tab ─────────────────────────────────────────────────────────────

const ContactsTab = ({ hospitalId, currentRole, showModal, setShowModal }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [editContact, setEditContact] = useState(null);
  const fetchedForId = useRef(null);

  const fetchContacts = useCallback(async (autoOpenIfEmpty = false) => {
    setLoading(true);
    setError("");
    try {
      const res = await getHospitalContacts(currentRole, hospitalId);
      const items = res?.msg?.items || res?.msg || res?.data || [];
      setContacts(items);
      if (autoOpenIfEmpty && items.length === 0) setShowModal(true);
    } catch (err) {
      setError(err.message || "Failed to load contacts.");
    } finally {
      setLoading(false);
    }
  }, [currentRole, hospitalId, setShowModal]);

  useEffect(() => {
    if (fetchedForId.current === hospitalId) return;
    fetchedForId.current = hospitalId;
    fetchContacts(true);
  }, [hospitalId, fetchContacts]);


  const handleSave = async (form) => {
    setSaveError("");
    try {
      await createHospitalContact(currentRole, hospitalId, form);
      setShowModal(false);
      await fetchContacts();
    } catch (err) {
      setSaveError(err.message || "Failed to save contact.");
      throw err;
    }
  };

  const handleEditSave = async (form) => {
    try {
      await editHospitalContact(currentRole, hospitalId, editContact.id, form);
      setEditContact(null);
      await fetchContacts();
    } catch (err) {
      throw err;
    }
  };

  const existingMobiles = contacts.map((c) => c.mobile).filter(Boolean);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          Contacts
          {contacts.length > 0 && (
            <span className="ml-2 text-xs text-gray-400 font-normal">
              ({contacts.length})
            </span>
          )}
        </h3>
        <button
          onClick={() => {
            setSaveError("");
            setShowModal(true);
          }}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          + Add Contact
        </button>
      </div>

      {saveError && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">
          {saveError}
        </div>
      )}

      {loading && (
        <p className="text-sm text-gray-500">Loading contacts...</p>
      )}
      {!loading && error && (
        <div className="text-sm text-red-700 bg-red-50 p-3 rounded-md">
          {error}
        </div>
      )}
      {!loading && !error && contacts.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-md">
          No contacts added yet. Click <strong>+ Add Contact</strong> to get started.
        </div>
      )}
      {!loading && contacts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {contacts.map((c, i) => (
            <ContactCard key={c.id ?? i} contact={c} onEdit={() => setEditContact(c)} />
          ))}
        </div>
      )}

      {showModal && (
        <ContactFormModal
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          existingMobiles={existingMobiles}
        />
      )}

      {editContact && (
        <EditContactFormModal
          contact={editContact}
          onClose={() => setEditContact(null)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

// ─── Documents ────────────────────────────────────────────────────────────────

const DOCUMENT_TYPES = [
  {
    label: "Hospital Registration Certificate",
    value: "hospital_registration_certificate",
    accept: ".pdf,image/jpeg,image/png",
    acceptLabel: "PDF / JPG / PNG",
    maxMB: 5,
  },
  {
    label: "Medical License",
    value: "hospital_medical_license",
    accept: ".pdf,image/jpeg,image/png",
    acceptLabel: "PDF / JPG / PNG",
    maxMB: 5,
  },
  {
    label: "Partnership Agreement",
    value: "hospital_partnership_agreement",
    accept: ".pdf,image/jpeg,image/png",
    acceptLabel: "PDF / JPG / PNG",
    maxMB: 10,
  },
  {
    label: "Cancelled Cheque",
    value: "hospital_cancelled_cheque",
    accept: ".pdf,image/jpeg,image/png",
    acceptLabel: "PDF / JPG / PNG",
    maxMB: 2,
  },
  {
    label: "Owner/Authorized Signatory ID Proof",
    value: "hospital_owner_id_proof",
    accept: ".pdf,image/jpeg,image/png",
    acceptLabel: "PDF / JPG / PNG",
    maxMB: 2,
  },
  {
    label: "Hospital Pan Card",
    value: "hospital_pan_card",
    accept: ".pdf,image/jpeg,image/png",
    acceptLabel: "PDF / JPG / PNG",
    maxMB: 5,
  },
];

const DOC_TYPE_MAP = Object.fromEntries(DOCUMENT_TYPES.map((d) => [d.value, d]));
const DOCUMENT_TYPE_VALUES = new Set(DOCUMENT_TYPES.map((d) => d.value));

// Validates that a File object matches an accept string (e.g. ".pdf,image/*")
const fileMatchesAccept = (file, accept) => {
  const tokens = accept.split(",").map((s) => s.trim().toLowerCase());
  const ext = "." + file.name.split(".").pop().toLowerCase();
  const mime = (file.type || "").toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith(".")) return ext === token;
    if (token.endsWith("/*")) return mime.startsWith(token.slice(0, -1));
    return mime === token;
  });
};

const DocumentUploadModal = ({ onClose, onUpload }) => {
  const [labelName, setLabelName] = useState("");
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  const selectedType = DOC_TYPE_MAP[labelName] || null;

  const validate = () => {
    const e = {};
    if (!labelName) e.labelName = "Please select a document type.";
    if (!file) {
      e.file = "Please choose a file.";
    } else if (selectedType) {
      if (!fileMatchesAccept(file, selectedType.accept)) {
        e.file = `Invalid file type. Accepted: ${selectedType.acceptLabel}.`;
      } else {
        const maxBytes = selectedType.maxMB * 1024 * 1024;
        if (file.size > maxBytes)
          e.file = `File exceeds maximum size of ${selectedType.maxMB} MB.`;
      }
    }
    return e;
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setErrors((prev) => ({ ...prev, file: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setUploading(true);
    try {
      await onUpload(file, labelName);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-semibold text-gray-900">Share Document</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Document Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Document Type <span className="text-red-500">*</span>
            </label>
            <select
              value={labelName}
              onChange={(e) => { setLabelName(e.target.value); setFile(null); setErrors({}); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select document type</option>
              {DOCUMENT_TYPES.map(({ label, value }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {errors.labelName && (
              <p className="text-red-500 text-xs mt-1">{errors.labelName}</p>
            )}
          </div>

          {/* File hint */}
          {selectedType && (
            <p className="text-xs text-gray-500">
              Accepted: <strong>{selectedType.acceptLabel}</strong> — max{" "}
              <strong>{selectedType.maxMB} MB</strong>
            </p>
          )}

          {/* File Input */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              File <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept={selectedType?.accept || ".pdf,image/*"}
              onChange={handleFileChange}
              key={labelName}
              className="w-full text-sm text-gray-700 border border-gray-300 rounded-md px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {errors.file && (
              <p className="text-red-500 text-xs mt-1">{errors.file}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? "Uploading..." : "Upload Document"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Contract Signing Authority ───────────────────────────────────────────────

const ContractSigningModal = ({ existing, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: existing?.name || "",
    mobile: existing?.mobile || "",
    signed_by: existing?.signed_by || "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.mobile.trim()) {
      e.mobile = "Mobile is required.";
    } else if (!/^\d{10}$/.test(form.mobile.trim())) {
      e.mobile = "Mobile must be exactly 10 digits.";
    }
    if (!form.signed_by.trim()) e.signed_by = "Signed by is required.";
    return e;
  };

  const handleChange = (field) => (ev) => {
    setForm((prev) => ({ ...prev, [field]: ev.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-semibold text-gray-900">
            Contract Signing Authority
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={handleChange("name")}
              placeholder="Full name"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Mobile <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              maxLength={10}
              value={form.mobile}
              onChange={(ev) => {
                setForm((prev) => ({ ...prev, mobile: ev.target.value.replace(/\D/g, "") }));
                setErrors((prev) => ({ ...prev, mobile: undefined }));
              }}
              placeholder="10-digit mobile number"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.mobile && <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Signed By <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.signed_by}
              onChange={handleChange("signed_by")}
              placeholder="Designation / role"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.signed_by && <p className="text-red-500 text-xs mt-1">{errors.signed_by}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditDocumentModal = ({ doc, accept, onClose, onSave }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError("Please select a file to replace."); return; }
    setSaving(true);
    setError("");
    try {
      await onSave(file);
    } catch (err) {
      setError(err.message || "Failed to replace file.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">Replace File</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <p className="text-xs text-gray-500">
            Replacing: <span className="font-medium text-gray-700">{doc.label?.display_name || "Document"}</span>
          </p>
          <div
            className="border-2 border-dashed border-gray-200 rounded-md p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors"
            onClick={() => fileRef.current?.click()}
          >
            {file ? (
              <p className="text-xs font-medium text-gray-700 truncate">{file.name}</p>
            ) : (
              <p className="text-xs text-gray-400">Click to select a new file</p>
            )}
            <input
              ref={fileRef}
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => { setFile(e.target.files[0] || null); setError(""); }}
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 rounded hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Replacing…" : "Replace File"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DocumentsTab = ({ hospitalId, currentRole, showModal, setShowModal }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const fetchedForId = useRef(null);

  const [editDoc, setEditDoc] = useState(null);

  const [csaData, setCsaData] = useState(null);
  const [csaLoading, setCsaLoading] = useState(true);
  const [showCsaModal, setShowCsaModal] = useState(false);
  const [csaError, setCsaError] = useState("");
  const [csaSuccessMsg, setCsaSuccessMsg] = useState("");

  const fetchDocuments = useCallback(async (autoOpenIfEmpty = false) => {
    setLoading(true);
    setError("");
    try {
      const res = await getHospitalDocuments(currentRole, hospitalId);
      const allItems = res?.msg?.items || res?.msg || res?.data || [];
      const items = allItems.filter((d) => DOCUMENT_TYPE_VALUES.has(d.label?.name));
      setDocuments(items);
      if (autoOpenIfEmpty && items.length === 0) setShowModal(true);
    } catch (err) {
      setError(err.message || "Failed to load documents.");
    } finally {
      setLoading(false);
    }
  }, [currentRole, hospitalId, setShowModal]);

  useEffect(() => {
    if (fetchedForId.current === hospitalId) return;
    fetchedForId.current = hospitalId;
    fetchDocuments(true);
  }, [hospitalId, fetchDocuments]);


  const fetchCsa = useCallback(async () => {
    setCsaLoading(true);
    setCsaError("");
    try {
      const res = await getContractSigningAuthority(currentRole, hospitalId);
      const data = res?.msg || {};
      setCsaData(Object.keys(data).length ? data : null);
    } catch {
      setCsaData(null); // treat any load failure as empty — empty state handles it
    } finally {
      setCsaLoading(false);
    }
  }, [currentRole, hospitalId]);

  useEffect(() => {
    fetchCsa();
  }, [fetchCsa]);


  const handleUpload = async (file, labelName) => {
    setUploadError("");
    setSuccessMsg("");
    try {
      await uploadHospitalDocument(currentRole, hospitalId, file, labelName);
      setShowModal(false);
      setSuccessMsg("Document uploaded successfully.");
      await fetchDocuments();
    } catch (err) {
      setUploadError(err.message || "Failed to upload document.");
      throw err;
    }
  };

  const handleDocReplace = async (file) => {
    await editHospitalDocument(currentRole, hospitalId, editDoc.id, file);
    setEditDoc(null);
    setSuccessMsg("Document replaced successfully.");
    await fetchDocuments();
  };

  const handleCsaSave = async (form) => {
    setCsaError("");
    setCsaSuccessMsg("");
    try {
      await saveContractSigningAuthority(currentRole, hospitalId, form);
      setShowCsaModal(false);
      setCsaSuccessMsg("Contract signing authority saved.");
      await fetchCsa();
    } catch (err) {
      setCsaError(err.message || "Failed to save contract signing authority.");
      throw err;
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          Documents
          {documents.length > 0 && (
            <span className="ml-2 text-xs text-gray-400 font-normal">
              ({documents.length})
            </span>
          )}
        </h3>
        <button
          onClick={() => { setUploadError(""); setSuccessMsg(""); setShowModal(true); }}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          + Share Document
        </button>
      </div>

      {uploadError && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">
          {uploadError}
        </div>
      )}
      {successMsg && (
        <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 p-3 rounded-md">
          {successMsg}
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Loading documents...</p>}
      {!loading && error && (
        <div className="text-sm text-red-700 bg-red-50 p-3 rounded-md">{error}</div>
      )}
      {!loading && !error && documents.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-md">
          No documents uploaded yet. Click <strong>+ Share Document</strong> to get started.
        </div>
      )}
      {!loading && documents.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {documents.map((doc, i) => {
            const fileName = doc.url ? doc.url.split("/").pop().replace(/^[a-z0-9]+_/, "") : null;
            const isImage = doc.url && /\.(png|jpg|jpeg|svg|webp)$/i.test(doc.url);
            const isPdf = doc.url && /\.pdf$/i.test(doc.url);
            return (
              <div
                key={doc.id ?? i}
                className="border border-gray-200 rounded-md bg-white shadow-sm flex flex-col overflow-hidden"
              >
                {/* Preview area */}
                <div className="h-28 bg-gray-50 flex items-center justify-center border-b border-gray-100">
                  {isImage ? (
                    <img
                      src={doc.url}
                      alt={fileName}
                      className="h-full w-full object-contain p-2"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-gray-300">
                      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                        {isPdf ? "PDF" : "File"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-3 flex-1 flex flex-col gap-1">
                  <p className="text-xs font-semibold text-gray-700 leading-tight">
                    {doc.label?.display_name || (
                      <span className="text-gray-400 italic">Unlabelled</span>
                    )}
                  </p>
                  {fileName && (
                    <p className="text-xs text-gray-400 truncate" title={fileName}>
                      {fileName}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-auto pt-1">
                    {doc.created_at
                      ? new Date(doc.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                      : ""}
                  </p>
                </div>

                {/* Footer */}
                <div className="px-3 pb-3 flex gap-2">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center text-xs font-medium text-blue-600 border border-blue-200 rounded py-1.5 hover:bg-blue-50 transition-colors"
                  >
                    View / Download
                  </a>
                  <button
                    onClick={() => setEditDoc(doc)}
                    className="px-2.5 text-xs font-medium text-gray-600 border border-gray-200 rounded py-1.5 hover:bg-gray-50 transition-colors"
                  >
                    Replace
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editDoc && (
        <EditDocumentModal
          doc={editDoc}
          accept={DOC_TYPE_MAP[editDoc.label?.name]?.accept || "*"}
          onClose={() => setEditDoc(null)}
          onSave={handleDocReplace}
        />
      )}

      {showModal && (
        <DocumentUploadModal
          onClose={() => setShowModal(false)}
          onUpload={handleUpload}
        />
      )}

      {/* Contract Signing Authority */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-gray-900">
            Contract Signing Authority
          </h3>
          <button
            onClick={() => { setCsaError(""); setCsaSuccessMsg(""); setShowCsaModal(true); }}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            {csaData ? "Edit" : "+ Add"}
          </button>
        </div>

        {csaSuccessMsg && (
          <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 p-3 rounded-md">
            {csaSuccessMsg}
          </div>
        )}
        {csaError && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">
            {csaError}
          </div>
        )}

        {csaLoading && <p className="text-sm text-gray-500">Loading...</p>}

        {!csaLoading && !csaData && (
          <div className="text-center py-10 text-gray-400 text-sm border border-dashed border-gray-200 rounded-md">
            No contract signing authority added yet. Click <strong>+ Add</strong> to get started.
          </div>
        )}

        {!csaLoading && csaData && (
          <div className="border border-gray-200 rounded-md bg-gray-50 p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Name</p>
              <p className="text-sm font-medium text-gray-800">{csaData.name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Mobile</p>
              <p className="text-sm font-medium text-gray-800">{csaData.mobile}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Signed By</p>
              <p className="text-sm font-medium text-gray-800">{csaData.signed_by}</p>
            </div>
          </div>
        )}
      </div>

      {showCsaModal && (
        <ContractSigningModal
          existing={csaData}
          onClose={() => setShowCsaModal(false)}
          onSave={handleCsaSave}
        />
      )}
    </div>
  );
};

// ─── Media & Management ───────────────────────────────────────────────────────

const MEDIA_TYPES = [
  {
    label: "Hospital Exterior / Interior Photo",
    value: "hospital_exterior_interior_photo",
    accept: "image/jpeg,image/png,image/webp",
    acceptLabel: "JPG, PNG, WebP",
    maxMB: 5,
    category: "image",
    maxCount: 20,
  },
  {
    label: "Equipment Photo",
    value: "equipment_photo",
    accept: "image/jpeg,image/png,image/webp",
    acceptLabel: "JPG, PNG, WebP",
    maxMB: 5,
    category: "image",
    maxCount: 10,
  },
  {
    label: "Doctor Photo",
    value: "doctor_photo",
    accept: "image/jpeg,image/png,image/webp",
    acceptLabel: "JPG, PNG, WebP",
    maxMB: 5,
    category: "image",
    maxCount: 50,
  },
  {
    label: "Virtual Hospital Tour",
    value: "virtual_hospital_tour",
    accept: "video/mp4,video/webm",
    acceptLabel: "MP4, WebM",
    maxMB: 50,
    category: "video",
    maxCount: 5,
  },
  {
    label: "Patient Testimonial",
    value: "patient_testimonial",
    accept: "video/mp4,video/webm",
    acceptLabel: "MP4, WebM",
    maxMB: 50,
    category: "video",
    maxCount: 10,
  },
];

const MEDIA_TYPE_MAP = Object.fromEntries(MEDIA_TYPES.map((m) => [m.value, m]));
const MEDIA_TYPE_VALUES = new Set(MEDIA_TYPES.map((m) => m.value));

const MediaUploadModal = ({ onClose, onUpload, countsByType }) => {
  const [labelName, setLabelName] = useState("");
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  const selectedType = MEDIA_TYPE_MAP[labelName] || null;
  const currentCount = labelName ? (countsByType[labelName] || 0) : 0;
  const atLimit = selectedType ? currentCount >= selectedType.maxCount : false;

  const validate = () => {
    const e = {};
    if (!labelName) {
      e.labelName = "Please select a media type.";
    } else if (atLimit) {
      e.labelName = `Maximum ${selectedType.maxCount} files allowed for this type.`;
    }
    if (!file) {
      e.file = "Please choose a file.";
    } else if (selectedType) {
      if (!fileMatchesAccept(file, selectedType.accept)) {
        e.file = `Invalid file type. Accepted: ${selectedType.acceptLabel}.`;
      } else {
        const maxBytes = selectedType.maxMB * 1024 * 1024;
        if (file.size > maxBytes)
          e.file = `File exceeds maximum size of ${selectedType.maxMB} MB.`;
      }
    }
    return e;
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    setErrors((prev) => ({ ...prev, file: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setUploading(true);
    try {
      await onUpload(file, labelName);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-semibold text-gray-900">Upload Media</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Media Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Media Type <span className="text-red-500">*</span>
            </label>
            <select
              value={labelName}
              onChange={(e) => { setLabelName(e.target.value); setFile(null); setErrors({}); }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select media type</option>
              {MEDIA_TYPES.map(({ label, value, maxCount }) => {
                const count = countsByType[value] || 0;
                const full = count >= maxCount;
                return (
                  <option key={value} value={value} disabled={full}>
                    {label} ({count}/{maxCount}){full ? " — Full" : ""}
                  </option>
                );
              })}
            </select>
            {errors.labelName && (
              <p className="text-red-500 text-xs mt-1">{errors.labelName}</p>
            )}
          </div>

          {/* Hint */}
          {selectedType && (
            <div className="text-xs text-gray-500 space-y-0.5">
              <p>Accepted: <strong>{selectedType.acceptLabel}</strong> — max <strong>{selectedType.maxMB} MB</strong></p>
              <p>Uploaded: <strong>{currentCount} / {selectedType.maxCount}</strong></p>
            </div>
          )}

          {/* File Input */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              File <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept={selectedType?.accept || "image/*,video/*"}
              onChange={handleFileChange}
              key={labelName}
              disabled={atLimit}
              className="w-full text-sm text-gray-700 border border-gray-300 rounded-md px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
            />
            {errors.file && (
              <p className="text-red-500 text-xs mt-1">{errors.file}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || atLimit}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MediaTab = ({ hospitalId, currentRole, showModal, setShowModal }) => {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [editMedia, setEditMedia] = useState(null);
  const fetchedForId = useRef(null);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getHospitalDocuments(currentRole, hospitalId);
      const items = res?.msg?.items || res?.msg || res?.data || [];
      setMediaItems(items.filter((d) => MEDIA_TYPE_VALUES.has(d.label?.name)));
    } catch (err) {
      setError(err.message || "Failed to load media.");
    } finally {
      setLoading(false);
    }
  }, [currentRole, hospitalId]);

  useEffect(() => {
    if (fetchedForId.current === hospitalId) return;
    fetchedForId.current = hospitalId;
    fetchMedia();
  }, [hospitalId, fetchMedia]);


  const countsByType = mediaItems.reduce((acc, item) => {
    const name = item.label?.name;
    if (name) acc[name] = (acc[name] || 0) + 1;
    return acc;
  }, {});

  const handleUpload = async (file, labelName) => {
    setUploadError("");
    setSuccessMsg("");
    try {
      await uploadHospitalDocument(currentRole, hospitalId, file, labelName);
      setShowModal(false);
      setSuccessMsg("Media uploaded successfully.");
      await fetchMedia();
    } catch (err) {
      setUploadError(err.message || "Failed to upload media.");
      throw err;
    }
  };

  const handleMediaReplace = async (file) => {
    await editHospitalDocument(currentRole, hospitalId, editMedia.id, file);
    setEditMedia(null);
    setSuccessMsg("Media replaced successfully.");
    await fetchMedia();
  };

  const filteredItems = activeTab === "all"
    ? mediaItems
    : mediaItems.filter((item) => item.label?.name === activeTab);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          Media & Management
          {mediaItems.length > 0 && (
            <span className="ml-2 text-xs text-gray-400 font-normal">
              ({mediaItems.length})
            </span>
          )}
        </h3>
        <button
          onClick={() => { setUploadError(""); setSuccessMsg(""); setShowModal(true); }}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          + Upload Media
        </button>
      </div>

      {/* Quota summary — click to filter */}
      <div className="mb-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
        {MEDIA_TYPES.map(({ label, value, maxCount, category }) => {
          const count = countsByType[value] || 0;
          const pct = Math.round((count / maxCount) * 100);
          const full = count >= maxCount;
          const active = activeTab === value;
          return (
            <div
              key={value}
              onClick={() => setActiveTab(active ? "all" : value)}
              className={`border rounded-md p-2 text-xs cursor-pointer transition-colors select-none ${active
                ? "border-blue-400 bg-blue-50 ring-1 ring-blue-300"
                : "border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/40"
                }`}
            >
              <p className={`leading-tight mb-1 truncate ${active ? "text-blue-600 font-medium" : "text-gray-500"}`} title={label}>{label}</p>
              <div className="flex items-center gap-1.5">
                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${full ? "bg-red-400" : "bg-blue-400"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className={`font-medium shrink-0 ${full ? "text-red-500" : "text-gray-700"}`}>
                  {count}/{maxCount}
                </span>
              </div>
              <p className="text-gray-400 mt-0.5 capitalize">{category}</p>
            </div>
          );
        })}
      </div>

      {uploadError && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">
          {uploadError}
        </div>
      )}
      {successMsg && (
        <div className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 p-3 rounded-md">
          {successMsg}
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Loading media...</p>}
      {!loading && error && (
        <div className="text-sm text-red-700 bg-red-50 p-3 rounded-md">{error}</div>
      )}
      {!loading && !error && filteredItems.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-md">
          {mediaItems.length === 0
            ? <>No media uploaded yet. Click <strong>+ Upload Media</strong> to get started.</>
            : "No media found for this category."}
        </div>
      )}
      {!loading && filteredItems.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {filteredItems.map((item, i) => {
            const typeInfo = MEDIA_TYPE_MAP[item.label?.name];
            const isVideo = typeInfo?.category === "video";
            const isImage = typeInfo?.category === "image";
            const fileName = item.url ? item.url.split("/").pop().replace(/^[a-z0-9]+_/, "") : null;
            return (
              <div
                key={item.id ?? i}
                className="border border-gray-200 rounded-md bg-white shadow-sm flex flex-col overflow-hidden"
              >
                {/* Preview */}
                <div className="h-32 bg-gray-50 flex items-center justify-center border-b border-gray-100 relative">
                  {isImage && item.url ? (
                    <img
                      src={item.url}
                      alt={fileName}
                      className="h-full w-full object-cover"
                    />
                  ) : isVideo ? (
                    <div className="flex flex-col items-center gap-1 text-gray-300">
                      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-6.586-3.8A1 1 0 007 8.232v7.536a1 1 0 001.166.984l6.586-1.9A1 1 0 0016 13.87v-2.74a1 1 0 00-.752-.962z" />
                        <circle cx="12" cy="12" r="9" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className="text-xs font-medium uppercase tracking-wide text-gray-400">Video</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-1 text-gray-300">
                      <svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="p-3 flex-1 flex flex-col gap-1">
                  <p className="text-xs font-semibold text-gray-700 leading-tight">
                    {typeInfo?.label || item.label?.display_name || (
                      <span className="text-gray-400 italic">Unknown</span>
                    )}
                  </p>
                  {fileName && (
                    <p className="text-xs text-gray-400 truncate" title={fileName}>
                      {fileName}
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-auto pt-1">
                    {item.created_at
                      ? new Date(item.created_at).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                      : ""}
                  </p>
                </div>

                {/* Footer */}
                <div className="px-3 pb-3 flex gap-2">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-center text-xs font-medium text-blue-600 border border-blue-200 rounded py-1.5 hover:bg-blue-50 transition-colors"
                  >
                    {isVideo ? "Play / Download" : "View / Download"}
                  </a>
                  <button
                    onClick={() => setEditMedia(item)}
                    className="px-2.5 text-xs font-medium text-gray-600 border border-gray-200 rounded py-1.5 hover:bg-gray-50 transition-colors"
                  >
                    Replace
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editMedia && (
        <EditDocumentModal
          doc={editMedia}
          accept={MEDIA_TYPE_MAP[editMedia.label?.name]?.accept || "*"}
          onClose={() => setEditMedia(null)}
          onSave={handleMediaReplace}
        />
      )}

      {showModal && (
        <MediaUploadModal
          onClose={() => setShowModal(false)}
          onUpload={handleUpload}
          countsByType={countsByType}
        />
      )}
    </div>
  );
};

// ─── Bank Tab ─────────────────────────────────────────────────────────────────

const ACCOUNT_TYPES = [
  { label: "Savings", value: "SA" },
  { label: "Current", value: "CA" },
];

const ACCOUNT_TYPE_LABEL = Object.fromEntries(
  ACCOUNT_TYPES.map(({ label, value }) => [value, label])
);

const EMPTY_BANK_FORM = {
  account_type: "",
  ifsc_code: "",
  account_number: "",
  account_name: "",
  bank_name: "",
  branch_name: "",
  branch_address: "",
};

const BankFormModal = ({ onClose, onSave, initialData }) => {
  const [form, setForm] = useState(
    initialData
      ? {
          account_type: initialData.account_type || "",
          ifsc_code: initialData.ifsc_code || "",
          account_number: initialData.account_number || "",
          account_name: initialData.account_name || initialData.account_holder_name || "",
          bank_name: initialData.bank_name || "",
          branch_name: initialData.branch_name || "",
          branch_address: initialData.branch_address || "",
        }
      : EMPTY_BANK_FORM
  );
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [ifscLoading, setIfscLoading] = useState(false);

  const fetchIfscDetails = async (ifsc) => {
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) return;
    setIfscLoading(true);
    try {
      const res = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
      if (res.ok) {
        const data = await res.json();
        setForm((f) => ({
          ...f,
          bank_name: data.BANK || f.bank_name,
          branch_name: data.BRANCH || f.branch_name,
          branch_address: data.ADDRESS || f.branch_address,
        }));
        setErrors((e) => ({ ...e, ifsc_code: "", bank_name: "", branch_name: "" }));
      } else {
        setErrors((e) => ({ ...e, ifsc_code: "Invalid IFSC code." }));
      }
    } catch {
      // silently fail, user can fill manually
    } finally {
      setIfscLoading(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.account_type) e.account_type = "Account type is required.";
    if (!form.ifsc_code.trim()) {
      e.ifsc_code = "IFSC code is required.";
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.ifsc_code.trim().toUpperCase())) {
      e.ifsc_code = "Enter a valid IFSC code (e.g. SBIN0001234).";
    }
    if (!form.account_number.trim()) {
      e.account_number = "Account number is required.";
    } else if (!/^\d{9,20}$/.test(form.account_number.trim())) {
      e.account_number = "Account number must be 9–20 digits.";
    }
    if (!form.account_name.trim()) e.account_name = "Account holder name is required.";
    if (!form.bank_name.trim()) e.bank_name = "Bank name is required.";
    if (!form.branch_name.trim()) e.branch_name = "Branch name is required.";
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await onSave({
        ...form,
        ifsc_code: form.ifsc_code.trim().toUpperCase(),
        account_number: form.account_number.trim(),
        account_name: form.account_name.trim(),
        bank_name: form.bank_name.trim(),
        branch_name: form.branch_name.trim(),
        branch_address: form.branch_address.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  const field = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: "" }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-semibold text-gray-900">{initialData ? "Edit Bank Account" : "Add Bank Account"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Account Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Account Type <span className="text-red-500">*</span>
            </label>
            <select
              value={form.account_type}
              onChange={(e) => field("account_type", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select account type</option>
              {ACCOUNT_TYPES.map(({ label, value }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {errors.account_type && <p className="text-red-500 text-xs mt-1">{errors.account_type}</p>}
          </div>

          {/* IFSC Code */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              IFSC Code <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={form.ifsc_code}
                onChange={(e) => {
                  const val = e.target.value.toUpperCase();
                  field("ifsc_code", val);
                  if (val.length === 11) fetchIfscDetails(val);
                }}
                maxLength={11}
                placeholder="e.g. SBIN0001234"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase"
              />
              {ifscLoading && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">Fetching...</span>
              )}
            </div>
            {errors.ifsc_code && <p className="text-red-500 text-xs mt-1">{errors.ifsc_code}</p>}
          </div>

          {/* Account Number */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Account Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.account_number}
              onChange={(e) => field("account_number", e.target.value.replace(/\D/g, ""))}
              maxLength={20}
              placeholder="Enter account number"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.account_number && <p className="text-red-500 text-xs mt-1">{errors.account_number}</p>}
          </div>

          {/* Account Holder Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Account Holder Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.account_name}
              onChange={(e) => field("account_name", e.target.value)}
              maxLength={255}
              placeholder="Name as on bank account"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.account_name && <p className="text-red-500 text-xs mt-1">{errors.account_name}</p>}
          </div>

          {/* Bank Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Bank Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.bank_name}
              onChange={(e) => field("bank_name", e.target.value)}
              maxLength={255}
              placeholder="e.g. State Bank of India"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.bank_name && <p className="text-red-500 text-xs mt-1">{errors.bank_name}</p>}
          </div>

          {/* Branch Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Branch Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.branch_name}
              onChange={(e) => field("branch_name", e.target.value)}
              maxLength={255}
              placeholder="e.g. MG Road Branch"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.branch_name && <p className="text-red-500 text-xs mt-1">{errors.branch_name}</p>}
          </div>

          {/* Branch Address */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Branch Address</label>
            <input
              type="text"
              value={form.branch_address}
              onChange={(e) => field("branch_address", e.target.value)}
              maxLength={255}
              placeholder="Full branch address (optional)"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : initialData ? "Save Changes" : "Save Bank Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Bank Card ────────────────────────────────────────────────────────────────

const BankCard = ({ bank }) => {
  const typeColors = {
    SA: "bg-blue-100 text-blue-700",
    CA: "bg-amber-100 text-amber-700",
  };
  const colorClass = typeColors[bank.account_type] || "bg-gray-100 text-gray-700";

  return (
    <div className="border border-gray-200 rounded-md p-4 bg-white shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        {bank.account_type && (
          <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${colorClass}`}>
            {ACCOUNT_TYPE_LABEL[bank.account_type] ?? bank.account_type}
          </span>
        )}
        <span className="text-xs text-gray-400 font-mono">{bank.account_number}</span>
      </div>
      <div className="space-y-1 text-sm">
        <div className="font-medium text-gray-900">{bank.account_name}</div>
        <div className="text-gray-500 font-mono text-xs">{bank.ifsc_code}</div>
        <div className="text-gray-600">{bank.bank_name}</div>
        {bank.branch_name && (
          <div className="text-gray-400 text-xs">{bank.branch_name}{bank.branch_address ? ` — ${bank.branch_address}` : ""}</div>
        )}
      </div>
    </div>
  );
};

// ─── Bank Tab (list + modal) ──────────────────────────────────────────────────

const BankTab = ({ hospitalId, currentRole, showModal, setShowModal }) => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [editBank, setEditBank] = useState(null);
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);
  const fetchedForId = useRef(null);

  const fetchBanks = useCallback(async (autoOpenIfEmpty = false) => {
    setLoading(true);
    setError("");
    try {
      const res = await getHospitalBank(currentRole, hospitalId);
      const items = res?.msg?.items || res?.msg || res?.data || [];
      const list = Array.isArray(items) ? items : [items].filter((i) => i && typeof i === "object" && i.account_number);
      setBanks(list);
      if (autoOpenIfEmpty && list.length === 0) setShowModal(true);
    } catch (err) {
      setError(err.message || "Failed to load bank details.");
    } finally {
      setLoading(false);
    }
  }, [currentRole, hospitalId, setShowModal]);

  useEffect(() => {
    if (fetchedForId.current === hospitalId) return;
    fetchedForId.current = hospitalId;
    fetchBanks(true);
  }, [hospitalId, fetchBanks]);


  const handleSave = async (form) => {
    setSaveError("");
    try {
      await saveHospitalBank(currentRole, hospitalId, form);
      setShowModal(false);
      await fetchBanks();
    } catch (err) {
      setSaveError(err.message || "Failed to save bank details.");
      throw err;
    }
  };

  const handleEditSave = async (form) => {
    setSaveError("");
    try {
      await editHospitalBank(currentRole, hospitalId, form);
      setEditBank(null);
      await fetchBanks();
    } catch (err) {
      setSaveError(err.message || "Failed to update bank details.");
      throw err;
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          Bank Accounts
          {banks.length > 0 && (
            <span className="ml-2 text-xs text-gray-400 font-normal">({banks.length})</span>
          )}
        </h3>
        <div className="flex gap-2">
          {banks.length > 0 && (
            <button
              onClick={() => { setSaveError(""); setEditBank(banks[0]); }}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Edit Bank
            </button>
          )}
          <button
            onClick={() => { setSaveError(""); if (banks.length > 0) setShowOverrideConfirm(true); else setShowModal(true); }}
            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            + Add Bank
          </button>
        </div>
      </div>

      {saveError && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">
          {saveError}
        </div>
      )}

      {loading && <p className="text-sm text-gray-500">Loading bank details...</p>}
      {!loading && error && (
        <div className="text-sm text-red-700 bg-red-50 p-3 rounded-md">{error}</div>
      )}
      {!loading && !error && banks.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-md">
          No bank accounts added yet. Click <strong>+ Add Bank</strong> to get started.
        </div>
      )}
      {!loading && banks.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {banks.map((b, i) => (
            <BankCard key={b.id ?? i} bank={b} />
          ))}
        </div>
      )}

      {showOverrideConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Override existing bank details?</h3>
                <p className="text-xs text-gray-500 mt-1">This hospital already has bank details on record. Adding new details will override the existing information. This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowOverrideConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowOverrideConfirm(false); setShowModal(true); }}
                className="px-4 py-2 text-sm text-white bg-amber-500 rounded-md hover:bg-amber-600"
              >
                Yes, Override
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <BankFormModal
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}

      {editBank && (
        <BankFormModal
          initialData={editBank}
          onClose={() => setEditBank(null)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

// ─── Treatment Package Form Modal ────────────────────────────────────────────

const TREATMENT_NAME_CHOICES = [
  ["cabg", "Cardiac Bypass Surgery (CABG)"],
  ["angioplasty", "Angioplasty (PTCA)"],
  ["heart_valve_replacement", "Heart Valve Replacement"],
  ["pacemaker_implantation", "Pacemaker Implantation"],
  ["asd_vsd_repair", "ASD / VSD Repair"],
  ["total_knee_replacement", "Total Knee Replacement"],
  ["total_hip_replacement", "Total Hip Replacement"],
  ["spine_surgery", "Spine Surgery (TLIF / PLIF)"],
  ["arthroscopy", "Arthroscopy"],
  ["shoulder_replacement", "Shoulder Replacement"],
  ["craniotomy", "Craniotomy"],
  ["brain_tumor_removal", "Brain Tumor Removal"],
  ["spinal_cord_surgery", "Spinal Cord Surgery"],
  ["deep_brain_stimulation", "Deep Brain Stimulation"],
  ["ventriculoperitoneal_shunt", "Ventriculoperitoneal Shunt"],
  ["liver_transplant", "Liver Transplant"],
  ["kidney_transplant", "Kidney Transplant"],
  ["bone_marrow_transplant", "Bone Marrow Transplant"],
  ["lung_transplant", "Lung Transplant"],
  ["cornea_transplant", "Cornea Transplant"],
  ["cholecystectomy", "Cholecystectomy (Gallbladder Removal)"],
  ["appendectomy", "Appendectomy"],
  ["hernia_repair", "Hernia Repair"],
  ["bariatric_surgery", "Bariatric Surgery"],
  ["colorectal_surgery", "Colorectal Surgery"],
  ["ercp", "ERCP"],
  ["whipple_procedure", "Whipple Procedure (Pancreatectomy)"],
  ["splenectomy", "Splenectomy"],
  ["hysterectomy", "Hysterectomy"],
  ["cesarean_section", "Cesarean Section (C-Section)"],
  ["normal_delivery", "Normal Delivery Package"],
  ["ivf_treatment", "IVF Treatment"],
  ["myomectomy", "Myomectomy"],
  ["laparoscopic_tubal_ligation", "Laparoscopic Tubal Ligation"],
  ["ovarian_cystectomy", "Ovarian Cystectomy"],
  ["cataract_surgery", "Cataract Surgery"],
  ["lasik_eye_surgery", "LASIK Eye Surgery"],
  ["glaucoma_surgery", "Glaucoma Surgery"],
  ["retinal_detachment_surgery", "Retinal Detachment Surgery"],
  ["vitrectomy", "Vitrectomy"],
  ["tonsillectomy", "Tonsillectomy"],
  ["septoplasty", "Septoplasty"],
  ["sinus_surgery", "Sinus Surgery (FESS)"],
  ["cochlear_implant", "Cochlear Implant"],
  ["rhinoplasty", "Rhinoplasty"],
  ["dialysis_monthly", "Dialysis Package (Monthly)"],
  ["dialysis_per_session", "Dialysis (Per Session)"],
  ["kidney_stone_removal", "Kidney Stone Removal (PCNL / RIRS)"],
  ["prostate_surgery", "Prostate Surgery (TURP)"],
  ["radical_prostatectomy", "Radical Prostatectomy"],
  ["circumcision", "Circumcision"],
  ["cystectomy", "Cystectomy"],
  ["mastectomy", "Mastectomy"],
  ["lumpectomy", "Lumpectomy"],
  ["thyroid_surgery", "Thyroid Surgery"],
  ["adrenalectomy", "Adrenalectomy"],
  ["amputation", "Amputation"],
  ["skin_graft", "Skin Graft"],
  ["cleft_lip_palate_surgery", "Cleft Lip / Palate Surgery"],
  ["breast_augmentation", "Breast Augmentation"],
  ["liposuction", "Liposuction"],
  ["abdominoplasty", "Abdominoplasty"],
  ["chemotherapy_per_cycle", "Chemotherapy Package (Per Cycle)"],
  ["radiation_therapy", "Radiation Therapy Package"],
  ["icu_daily", "ICU Package (Daily)"],
  ["nicu_package", "Neonatal ICU (NICU) Package"],
];

const TREATMENT_NAME_LABEL = Object.fromEntries(TREATMENT_NAME_CHOICES);

const EMPTY_PKG_FORM = {
  name: "",
  inclusion: "",
  duration_in_day: "",
};

const toIndianFormat = (raw) => {
  const digits = String(raw).replace(/[^0-9]/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("en-IN");
};

const stripCommas = (val) => String(val).replace(/,/g, "");

const TreatmentPackageFormModal = ({ onClose, onSave, existingNames = new Set() }) => {
  const [form, setForm] = useState(EMPTY_PKG_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Package name is required.";
    else if (existingNames.has(form.name)) e.name = "This treatment package already exists for this hospital.";
    if (!form.duration_in_day || isNaN(Number(form.duration_in_day)) || Number(form.duration_in_day) <= 0)
      e.duration_in_day = "Enter a valid number of days.";
    return e;
  };

  const field = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setSaving(true);
    setSaveError("");
    try {
      await onSave({
        name: form.name,
        inclusion: form.inclusion,
        duration_in_day: Number(form.duration_in_day),
      });
    } catch (err) {
      const raw = err.message || "";
      let msg = "";
      try {
        const jsonStart = raw.indexOf("{");
        if (jsonStart !== -1) {
          const parsed = JSON.parse(raw.slice(jsonStart));
          const apiErrors = parsed?.msg;
          if (Array.isArray(apiErrors))
            msg = apiErrors.map((e) => e.value || e.msg || "").filter(Boolean).join(" ");
        }
      } catch {
        // not JSON — fall through to raw message
      }
      setSaveError(msg || raw.replace(/^API Error \d+:\s*/i, "") || "Failed to save package.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-semibold text-gray-900">Add Treatment Package</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {saveError && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{saveError}</div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Treatment Name <span className="text-red-500">*</span>
            </label>
            <select
              value={form.name}
              onChange={(e) => field("name", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select treatment</option>
              {TREATMENT_NAME_CHOICES.map(([value, label]) => (
                <option key={value} value={value} disabled={existingNames.has(value)}>
                  {label}{existingNames.has(value) ? " (Already Added)" : ""}
                </option>
              ))}
            </select>
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Inclusions</label>
            <textarea
              value={form.inclusion}
              onChange={(e) => field("inclusion", e.target.value)}
              placeholder="e.g. Surgery, ICU stay, medicines, physiotherapy"
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Estimated Duration (days) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              value={form.duration_in_day}
              onChange={(e) => field("duration_in_day", e.target.value)}
              placeholder="7"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.duration_in_day && (
              <p className="text-red-500 text-xs mt-1">{errors.duration_in_day}</p>
            )}
          </div>

          <p className="text-xs text-gray-400 bg-gray-50 border border-gray-100 p-2.5 rounded-md">
            Room prices are added to the package after creation via the package card.
          </p>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving..." : "Save Package"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Create Room Modal ────────────────────────────────────────────────────────

const STANDARD_ROOM_TYPES = [
  ["general", "General Ward"],
  ["private", "Private Room"],
  ["deluxe", "Deluxe Room"],
  ["suite", "Suite"],
];

const MAX_PRICE = 9999999999.99;

const CreateRoomModal = ({ onClose, onSave, existingRoomTypes = new Set(), existingRoomNames = new Set() }) => {
  const [form, setForm] = useState(() => {
    const firstAvailable = STANDARD_ROOM_TYPES.find(([v]) => !existingRoomTypes.has(v))?.[0] ?? "general";
    return { mode: "standard", room_type: firstAvailable, name: "", base_price: "" };
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const validate = () => {
    const e = {};
    if (form.mode === "custom" && !form.name.trim()) e.name = "Room name is required.";
    else if (form.mode === "custom" && existingRoomNames.has(form.name.trim().toLowerCase())) e.name = "Room already exists.";
    if (!form.base_price || isNaN(Number(stripCommas(form.base_price))) || Number(stripCommas(form.base_price)) <= 0)
      e.base_price = "Enter a valid base price.";
    else if (Number(stripCommas(form.base_price)) > MAX_PRICE)
      e.base_price = "Base price is too large. Maximum allowed is 12 digit only.";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setSaving(true);
    setSaveError("");
    try {
      await onSave(
        form.mode === "custom"
          ? { mode: "custom", name: form.name.trim(), base_price: parseFloat(stripCommas(form.base_price)).toFixed(2) }
          : { mode: "standard", room_type: form.room_type, base_price: parseFloat(stripCommas(form.base_price)).toFixed(2) }
      );
    } catch (err) {
      const msg = err.message || "";
      setSaveError(msg.toLowerCase().includes("already exists") || msg.includes("duplicate key") ? "Room already exists." : msg || "Failed to create room.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-gray-900">Add Room</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        {saveError && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{saveError}</div>
        )}
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Mode: Standard vs Custom */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-2">Room Kind</label>
            <div className="flex gap-3">
              {[["standard", "Standard"], ["custom", "Custom"]].map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, mode: val, name: "", errors: {} }))}
                  className={`flex-1 py-2 text-sm rounded-md border transition-colors ${
                    form.mode === val
                      ? "bg-blue-600 text-white border-blue-600"
                      : "border-gray-300 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {/* Standard: room_type dropdown only */}
          {form.mode === "standard" && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Room Type <span className="text-red-500">*</span>
              </label>
              <select
                value={form.room_type}
                onChange={(e) => setForm((f) => ({ ...f, room_type: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {STANDARD_ROOM_TYPES.map(([value, label]) => (
                  <option key={value} value={value} disabled={existingRoomTypes.has(value)}>
                    {label}{existingRoomTypes.has(value) ? " (Already Added)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Custom: name field only */}
          {form.mode === "custom" && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Room Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setErrors((er) => ({ ...er, name: "" })); }}
                placeholder="e.g. VIP Recovery Room"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Base Price (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={toIndianFormat(form.base_price)}
              onChange={(e) => {
                const raw = stripCommas(e.target.value).replace(/[^\d.]/g, "");
                if (raw.replace(/\D/g, "").length > 12) return;
                setForm((f) => ({ ...f, base_price: raw }));
              }}
              placeholder="0"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.base_price && <p className="text-red-500 text-xs mt-1">{errors.base_price}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving..." : "Add Room"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Add Room Price Modal ─────────────────────────────────────────────────────

const AddRoomPriceModal = ({ pkg, rooms, onClose, onSave }) => {
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [price, setPrice] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const addedRoomIds = new Set((pkg.room_prices || []).map((rp) => rp.room?.id).filter(Boolean));

  const validate = () => {
    const e = {};
    if (!selectedRoom) e.room = "Select a room.";
    const numPrice = Number(stripCommas(price));
    if (!price || isNaN(numPrice)) e.price = "Enter a valid price.";
    else if (numPrice < 0) e.price = "Price cannot be negative.";
    else if (numPrice === 0) e.price = "Price must be greater than zero.";
    else if (numPrice > MAX_PRICE) e.price = "Base price is too large. Maximum allowed is 12 digit only.";
    else if (selectedRoom && Number(selectedRoom.base_price) > 0 && numPrice > Number(selectedRoom.base_price))
      e.price = `Room price cannot exceed the room's base price of ₹${Number(selectedRoom.base_price).toLocaleString("en-IN")}.`;
    return e;
  };

  const handleSelect = (room) => {
    setSelectedRoom(room);
    setPrice("");
    setErrors({});
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setSaving(true);
    setSaveError("");
    try {
      await onSave({ room: selectedRoom.id, price: parseFloat(stripCommas(price)).toFixed(2) });
    } catch (err) {
      setSaveError(err.message || "Failed to add room price.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Add Room Price</h3>
            <p className="text-xs text-gray-400 mt-0.5">{TREATMENT_NAME_LABEL[pkg.name] ?? pkg.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {saveError && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{saveError}</div>
        )}

        {rooms.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center">No rooms found. Add hospital rooms first.</p>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Select Room</label>
              <div className="space-y-1.5 max-h-44 overflow-y-auto pr-0.5">
                {rooms.map((r) => {
                  const isDisabled = r.category !== "custom" && addedRoomIds.has(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => !isDisabled && handleSelect(r)}
                      className={`w-full text-left px-3 py-2.5 text-sm rounded-md border transition-colors ${
                        isDisabled
                          ? "border-gray-100 text-gray-300 bg-gray-50 cursor-not-allowed"
                          : selectedRoom?.id === r.id
                          ? "bg-blue-50 border-blue-500 text-blue-800"
                          : "border-gray-200 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span className="font-medium">{r.name}</span>
                      <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        r.category === "custom" ? "bg-purple-100 text-purple-600" : "bg-gray-100 text-gray-500"
                      }`}>
                        {r.category}
                      </span>
                      <span className="float-right text-gray-400 text-xs">
                        {isDisabled ? "already added" : `₹${Number(r.base_price).toLocaleString("en-IN")}`}
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.room && <p className="text-red-500 text-xs mt-1">{errors.room}</p>}
            </div>

            {selectedRoom && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Package Price for <span className="text-blue-600">{selectedRoom.name}</span> (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={toIndianFormat(price)}
                  onChange={(e) => {
                    const raw = stripCommas(e.target.value).replace(/[^\d.]/g, "");
                    if (raw.replace(/\D/g, "").length > 12) return;
                    setPrice(raw);
                    setErrors((er) => ({ ...er, price: "" }));
                  }}
                  placeholder="0"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
                Cancel
              </button>
              <button type="submit" disabled={saving || !selectedRoom} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                {saving ? "Saving..." : "Add Price"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// ─── Update Package Modal ─────────────────────────────────────────────────────

const UpdatePackageModal = ({ pkg, onClose, onSave }) => {
  const [form, setForm] = useState({
    inclusion: pkg.inclusion || "",
    duration_in_day: pkg.duration_in_day ? String(pkg.duration_in_day) : "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const validate = () => {
    const e = {};
    if (!form.duration_in_day || isNaN(Number(form.duration_in_day)) || Number(form.duration_in_day) <= 0)
      e.duration_in_day = "Enter a valid number of days.";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e2 = validate();
    if (Object.keys(e2).length) { setErrors(e2); return; }
    setSaving(true);
    setSaveError("");
    try {
      await onSave({
        inclusion: form.inclusion,
        duration_in_day: Number(form.duration_in_day),
      });
    } catch (err) {
      setSaveError(err.message || "Failed to update package.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Edit Package</h3>
            <p className="text-xs text-gray-400 mt-0.5">{TREATMENT_NAME_LABEL[pkg.name] ?? pkg.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        {saveError && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{saveError}</div>
        )}
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Inclusions</label>
            <textarea
              value={form.inclusion}
              onChange={(e) => setForm((f) => ({ ...f, inclusion: e.target.value }))}
              placeholder="e.g. Surgery, ICU stay, medicines, physiotherapy"
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Estimated Duration (days) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              value={form.duration_in_day}
              onChange={(e) => setForm((f) => ({ ...f, duration_in_day: e.target.value }))}
              placeholder="7"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.duration_in_day && (
              <p className="text-red-500 text-xs mt-1">{errors.duration_in_day}</p>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving..." : "Update Package"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Edit Room Price Modal ────────────────────────────────────────────────────

const EditRoomPriceModal = ({ roomName, currentPrice, roomBasePrice, onClose, onSave }) => {
  const [price, setPrice] = useState(currentPrice ?? "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const numPrice = Number(price);
    if (!price || isNaN(numPrice)) { setError("Enter a valid price."); return; }
    if (numPrice < 0) { setError("Price cannot be negative."); return; }
    if (numPrice === 0) { setError("Price must be greater than zero."); return; }
    if (numPrice > MAX_PRICE) { setError("Base price is too large. Maximum allowed is 12 digit only."); return; }
    if (roomBasePrice && Number(roomBasePrice) > 0 && numPrice > Number(roomBasePrice)) {
      setError(`Room price cannot exceed the room's base price of ₹${Number(roomBasePrice).toLocaleString("en-IN")}.`);
      return;
    }
    setSaving(true);
    try {
      await onSave(parseFloat(price).toFixed(2));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xs mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Edit Room Price</h3>
            {roomName && <p className="text-xs text-gray-400 mt-0.5">{roomName}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Price (₹) <span className="text-red-500">*</span></label>
            <input
              type="number"
              min="1"
              step="0.01"
              value={price}
              onChange={(e) => {
                if (e.target.value.replace(/\D/g, "").length > 12) return;
                setPrice(e.target.value);
                setError("");
              }}
              placeholder="e.g. 35000"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
            />
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Treatment Package Card ───────────────────────────────────────────────────

const TreatmentPackageCard = ({ pkg, onAddRoomPrice, onEdit, onEditRoomPrice }) => {
  const roomPrices = pkg.room_prices || pkg.rooms || [];
  return (
    <div className="flex flex-col border border-gray-200 rounded-lg bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between px-4 pt-4 pb-3 border-b border-gray-100">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 truncate">
            {TREATMENT_NAME_LABEL[pkg.name] ?? pkg.name}
          </h4>
          {pkg.inclusion && (
            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed line-clamp-2">{pkg.inclusion}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 ml-3">
          {pkg.duration_in_day && (
            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">
              {pkg.duration_in_day}d
            </span>
          )}
          <button
            onClick={onEdit}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
            </svg>
            Edit
          </button>
        </div>
      </div>

      {/* Room Prices */}
      <div className="flex-1 px-4 py-3">
        {roomPrices.length > 0 ? (
          <div className="space-y-2">
            {roomPrices.map((r, i) => (
              <div key={r.room?.id ?? i} className="flex items-center justify-between">
                <span className="text-xs text-gray-500 flex items-center gap-1.5">
                  {r.room?.name ?? "—"}
                  {r.room?.room_type === "custom" && (
                    <span className="text-[10px] text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full font-medium">custom</span>
                  )}
                </span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-gray-800">₹{Number(r.price).toLocaleString("en-IN")}</span>
                  <button
                    onClick={() => onEditRoomPrice(r)}
                    className="p-1 rounded text-gray-300 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    title="Edit price"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">No room prices added yet.</p>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50 rounded-b-lg">
        <button
          onClick={onAddRoomPrice}
          className="w-full flex items-center justify-center gap-1.5 text-xs text-blue-600 font-medium py-0.5 rounded hover:bg-blue-50 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add Room Price
        </button>
      </div>
    </div>
  );
};

// ─── Treatment Packages Tab ───────────────────────────────────────────────────

const EditRoomModal = ({ room, onClose, onSave, existingRoomTypes = new Set() }) => {
  const isCustom = room.room_type === "custom" || room.category === "custom";
  const [form, setForm] = useState({
    room_type: room.room_type || "general",
    base_price: room.base_price ?? room.price ?? "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.base_price || isNaN(Number(form.base_price)) || Number(form.base_price) <= 0)
      e.base_price = "Enter a valid base price.";
    else if (Number(form.base_price) > MAX_PRICE)
      e.base_price = "Base price is too large. Maximum allowed is 12 digit only.";
    if (!isCustom && existingRoomTypes.has(form.room_type))
      e.room_type = `A ${STANDARD_ROOM_TYPES.find(([v]) => v === form.room_type)?.[1] ?? form.room_type} already exists.`;
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await onSave({
        base_price: parseFloat(form.base_price).toFixed(2),
        ...(isCustom ? {} : { room_type: form.room_type }),
      });
    } finally {
      setSaving(false);
    }
  };

  const cls = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-semibold text-gray-900">Edit Room</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {!isCustom && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Room Type</label>
              <select value={form.room_type} onChange={(e) => setForm((f) => ({ ...f, room_type: e.target.value }))} className={cls}>
                {STANDARD_ROOM_TYPES.map(([value, label]) => (
                  <option key={value} value={value} disabled={existingRoomTypes.has(value)}>
                    {label}{existingRoomTypes.has(value) ? " (Already Added)" : ""}
                  </option>
                ))}
              </select>
              {errors.room_type && <p className="text-red-500 text-xs mt-1">{errors.room_type}</p>}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Base Price (₹) <span className="text-red-500">*</span></label>
            <input
              type="number"
              min="1"
              step="0.01"
              value={form.base_price}
              onChange={(e) => {
                if (e.target.value.replace(/\D/g, "").length > 12) return;
                setForm((f) => ({ ...f, base_price: e.target.value }));
              }}
              placeholder="e.g. 2500"
              className={cls}
            />
            {errors.base_price && <p className="text-red-500 text-xs mt-1">{errors.base_price}</p>}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TreatmentPackagesTab = ({ hospitalId, currentRole, showModal, setShowModal }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [roomPkg, setRoomPkg] = useState(null);
  const [editPkg, setEditPkg] = useState(null);
  const [editRoomPriceTarget, setEditRoomPriceTarget] = useState(null); // { pkg, roomPrice }
  const [allPackageNames, setAllPackageNames] = useState(new Set());
  const fetchedForId = useRef(null);

  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState("");
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [editRoom, setEditRoom] = useState(null);

  const ITEMS_PER_PAGE = 10;

  const fetchPackages = useCallback(async (pageNum, nameFilter) => {
    setLoading(true);
    setError("");
    try {
      const res = await getTreatmentPackages(currentRole, hospitalId, {
        page: pageNum,
        items: ITEMS_PER_PAGE,
        name: nameFilter || undefined,
      });
      const items = res?.msg?.items || res?.msg || res?.data || [];
      const totalPages = res?.msg?.total_pages ?? res?.total_pages;
      setPackages(Array.isArray(items) ? items : []);
      setHasMore(totalPages ? pageNum < totalPages : items.length === ITEMS_PER_PAGE);
    } catch (err) {
      setError(err.message || "Failed to load treatment packages.");
    } finally {
      setLoading(false);
    }
  }, [currentRole, hospitalId]);

  const fetchRooms = useCallback(async () => {
    setRoomsLoading(true);
    setRoomsError("");
    try {
      const res = await getHospitalRooms(currentRole, hospitalId);
      const items = res?.msg?.items || res?.msg || res?.data || [];
      setRooms(Array.isArray(items) ? items : []);
    } catch (err) {
      setRoomsError(err.message || "Failed to load rooms.");
    } finally {
      setRoomsLoading(false);
    }
  }, [currentRole, hospitalId]);

  const fetchAllPackageNames = useCallback(async () => {
    try {
      const res = await getTreatmentPackages(currentRole, hospitalId, { page: 1, items: 100 });
      const items = res?.msg?.items || res?.msg || res?.data || [];
      setAllPackageNames(new Set((Array.isArray(items) ? items : []).map((p) => p.name).filter(Boolean)));
    } catch {
      // silently ignore — validate + error handling acts as fallback
    }
  }, [currentRole, hospitalId]);

  useEffect(() => {
    if (fetchedForId.current === hospitalId) return;
    fetchedForId.current = hospitalId;
    fetchPackages(1, "");
    fetchRooms();
    fetchAllPackageNames();
  }, [hospitalId, fetchPackages, fetchRooms, fetchAllPackageNames]);


  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchPackages(newPage, "");
  };

  const handleSave = async (form) => {
    await createTreatmentPackage(currentRole, hospitalId, form);
    setAllPackageNames((prev) => new Set([...prev, form.name]));
    setShowModal(false);
    setPage(1);
    await fetchPackages(1, "");
  };

  const handleUpdatePackage = async (payload) => {
    await updateTreatmentPackage(currentRole, hospitalId, editPkg.id, payload);
    setEditPkg(null);
    await fetchPackages(page, "");
  };

  const handleAddRoomPrice = async (payload) => {
    await addRoomPriceToPackage(currentRole, hospitalId, roomPkg.id, payload);
    setRoomPkg(null);
    await fetchPackages(page, "");
  };

  const handleEditRoomPrice = async (price) => {
    const { pkg, roomPrice } = editRoomPriceTarget;
    await editPackageRoomPrice(currentRole, hospitalId, pkg.id, roomPrice.room.id, { price });
    setEditRoomPriceTarget(null);
    await fetchPackages(page, "");
  };

  const handleSaveRoom = async (payload) => {
    if (payload.mode === "custom") {
      await addCustomRoom(currentRole, hospitalId, { name: payload.name, base_price: payload.base_price });
    } else {
      await createHospitalRoom(currentRole, hospitalId, { name: payload.name, room_type: payload.room_type, base_price: payload.base_price });
    }
    setShowCreateRoomModal(false);
    await fetchRooms();
  };

  const handleEditRoom = async (payload) => {
    await editHospitalRoom(currentRole, hospitalId, editRoom.id, payload);
    setEditRoom(null);
    await fetchRooms();
  };

  return (
    <div className="space-y-6">
      {/* ── Rooms ── */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold text-gray-900">
            Rooms
            {rooms.length > 0 && (
              <span className="ml-2 text-xs text-gray-400 font-normal">({rooms.length})</span>
            )}
          </h3>
          <button
            onClick={() => setShowCreateRoomModal(true)}
            className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            + Add Room
          </button>
        </div>

        {roomsLoading && <p className="text-sm text-gray-500">Loading rooms...</p>}
        {!roomsLoading && roomsError && (
          <div className="text-sm text-red-700 bg-red-50 p-3 rounded-md">{roomsError}</div>
        )}
        {!roomsLoading && !roomsError && rooms.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm border border-dashed border-gray-200 rounded-md">
            No rooms added yet.
          </div>
        )}
        {!roomsLoading && rooms.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {rooms.map((room, i) => (
              <div key={room.id ?? i} className="group relative border border-gray-200 rounded-md p-3 pb-8 bg-white shadow-sm text-sm overflow-hidden">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-gray-800">{room.name || room.room_type || "Standard"}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                    room.room_type === "custom" || room.category === "custom"
                      ? "bg-purple-100 text-purple-600"
                      : "bg-green-100 text-green-700"
                  }`}>
                    {room.room_type ?? room.category ?? "standard"}
                  </span>
                </div>
                {(room.base_price || room.price) && (
                  <p className="text-gray-400 mt-1 text-xs">
                    Base: ₹{Number(room.base_price ?? room.price).toLocaleString("en-IN")}
                  </p>
                )}
                <div className="absolute bottom-2 inset-x-3 flex justify-end translate-y-10 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 ease-out">
                  <button
                    onClick={() => setEditRoom(room)}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 border border-gray-200 rounded-full bg-white shadow-sm hover:bg-gray-50"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Treatment Packages ── */}
      <div>
        <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-gray-900">
            Treatment Packages
          </h3>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              + Add Package
            </button>
          </div>
        </div>

        {loading && <p className="text-sm text-gray-500">Loading packages...</p>}
        {!loading && error && (
          <div className="text-sm text-red-700 bg-red-50 p-3 rounded-md">{error}</div>
        )}
        {!loading && !error && packages.length === 0 && (
          <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-md">
            No treatment packages added yet. Click <strong>+ Add Package</strong> to get started.
          </div>
        )}
        {!loading && packages.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {packages.map((pkg, i) => (
              <TreatmentPackageCard
                key={pkg.id ?? i}
                pkg={pkg}
                onAddRoomPrice={() => setRoomPkg(pkg)}
                onEdit={() => setEditPkg(pkg)}
                onEditRoomPrice={(roomPrice) => setEditRoomPriceTarget({ pkg, roomPrice })}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && (page > 1 || hasMore) && (
          <div className="flex justify-between items-center mt-5">
            <button
              disabled={page === 1}
              onClick={() => handlePageChange(page - 1)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 disabled:opacity-40 hover:bg-gray-50"
            >
              ← Prev
            </button>
            <span className="text-xs text-gray-400">Page {page}</span>
            <button
              disabled={!hasMore}
              onClick={() => handlePageChange(page + 1)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-600 disabled:opacity-40 hover:bg-gray-50"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <TreatmentPackageFormModal
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          existingNames={allPackageNames}
        />
      )}

      {editPkg && (
        <UpdatePackageModal
          pkg={editPkg}
          onClose={() => setEditPkg(null)}
          onSave={handleUpdatePackage}
        />
      )}

      {roomPkg && (
        <AddRoomPriceModal
          pkg={roomPkg}
          rooms={rooms}
          onClose={() => setRoomPkg(null)}
          onSave={handleAddRoomPrice}
        />
      )}

      {editRoomPriceTarget && (
        <EditRoomPriceModal
          roomName={editRoomPriceTarget.roomPrice.room?.name}
          currentPrice={editRoomPriceTarget.roomPrice.price}
          roomBasePrice={editRoomPriceTarget.roomPrice.room?.base_price}
          onClose={() => setEditRoomPriceTarget(null)}
          onSave={handleEditRoomPrice}
        />
      )}

      {showCreateRoomModal && (
        <CreateRoomModal
          onClose={() => setShowCreateRoomModal(false)}
          onSave={handleSaveRoom}
          existingRoomTypes={new Set(rooms.map((r) => r.room_type).filter(Boolean))}
          existingRoomNames={new Set(rooms.map((r) => r.name?.toLowerCase()).filter(Boolean))}
        />
      )}

      {editRoom && (
        <EditRoomModal
          room={editRoom}
          onClose={() => setEditRoom(null)}
          onSave={handleEditRoom}
          existingRoomTypes={new Set(rooms.filter((r) => r.id !== editRoom.id).map((r) => r.room_type).filter(Boolean))}
        />
      )}
    </div>
  );
};

// ─── Financial Config Form Modal ─────────────────────────────────────────────

const PAYMENT_TERMS = [
  { label: "Net 15", value: "net_15" },
  { label: "Net 30", value: "net_30" },
  { label: "Net 45", value: "net_45" },
];

const MAX_DIAGNOSTIC_NAME_LEN = 50;

const EMPTY_FC_FORM = {
  base_consult_fee: "",
  emergency_charge: "",
  commission_in_percentage: "",
  payment_term: "",
  contract_start_at: "",
  contract_expiry_at: "",
  has_auto_renewal: false,
};

const FinancialConfigFormModal = ({ onClose, onSave, existing }) => {
  const [form, setForm] = useState(() => {
    if (!existing) return EMPTY_FC_FORM;
    return {
      base_consult_fee: existing.base_consult_fee ?? "",
      emergency_charge: existing.emergency_charge ?? "",
      commission_in_percentage: existing.commission_in_percentage != null ? String(existing.commission_in_percentage) : "",
      payment_term: existing.payment_term ?? "",
      contract_start_at: existing.contract_start_at ?? "",
      contract_expiry_at: existing.contract_expiry_at ?? "",
      has_auto_renewal: existing.has_auto_renewal ?? false,
    };
  });
  const [diagRows, setDiagRows] = useState(() => {
    const pricing = existing?.diagnostic_test_pricing;
    if (pricing && typeof pricing === "object" && Object.keys(pricing).length > 0) {
      return Object.entries(pricing).map(([key, value]) => ({ key, value: String(value) }));
    }
    return [{ key: "", value: "" }];
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const field = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: "" }));
  };

  const validate = () => {
    const e = {};

    if (form.commission_in_percentage !== "" && form.commission_in_percentage != null) {
      const commission = Number(form.commission_in_percentage);
      if (isNaN(commission)) e.commission_in_percentage = "Enter a valid commission rate (0–100).";
      else if (commission < 0) e.commission_in_percentage = "Commission rate cannot be negative.";
      else if (commission > 100) e.commission_in_percentage = "Commission rate cannot exceed 100%.";
    }

    const today = new Date().toISOString().split("T")[0];
    if (!form.contract_start_at) e.contract_start_at = "Contract start date is required.";
    else if (form.contract_start_at > today) e.contract_start_at = "Contract start date cannot be a future date.";
    if (!form.contract_expiry_at) e.contract_expiry_at = "Contract expiry date is required.";
    if (form.contract_start_at && form.contract_expiry_at && form.contract_expiry_at <= form.contract_start_at)
      e.contract_expiry_at = "Expiry date must be after start date.";

    diagRows.forEach((row, i) => {
      if (row.key.trim() || row.value.trim()) {
        if (row.key.trim().length > MAX_DIAGNOSTIC_NAME_LEN) {
          e[`diag_${i}`] = `Test name cannot exceed ${MAX_DIAGNOSTIC_NAME_LEN} characters.`;
          return;
        }
        if (row.value.trim()) {
          const v = Number(row.value);
          if (isNaN(v)) e[`diag_${i}`] = "Enter a valid price.";
          else if (v < 0) e[`diag_${i}`] = "Price cannot be negative.";
        }
      }
    });
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    setSaveError("");
    const diagnostic_test_pricing = {};
    diagRows.forEach(({ key, value }) => {
      if (key.trim() && value.trim()) diagnostic_test_pricing[key.trim()] = value.trim();
    });
    try {
      await onSave({
        base_consult_fee: form.base_consult_fee,
        emergency_charge: form.emergency_charge,
        diagnostic_test_pricing,
        commission_in_percentage: parseFloat(form.commission_in_percentage),
        payment_term: form.payment_term,
        contract_start_at: form.contract_start_at,
        contract_expiry_at: form.contract_expiry_at,
        has_auto_renewal: form.has_auto_renewal,
      });
    } catch (err) {
      setSaveError(err.message || "Failed to save financial config.");
    } finally {
      setSaving(false);
    }
  };

  const addDiagRow = () => setDiagRows((r) => [...r, { key: "", value: "" }]);
  const removeDiagRow = (i) => setDiagRows((r) => r.filter((_, idx) => idx !== i));
  const updateDiagRow = (i, field, val) =>
    setDiagRows((r) => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-base font-semibold text-gray-900">
            {existing ? "Edit Financial Config" : "Set Financial Config"}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {saveError && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{saveError}</div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {/* Base Consult Fee */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Base Consult Fee (₹)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.base_consult_fee}
              onChange={(e) => field("base_consult_fee", e.target.value)}
              placeholder="e.g. 500.00"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.base_consult_fee && <p className="text-red-500 text-xs mt-1">{errors.base_consult_fee}</p>}
          </div>

          {/* Emergency Charge */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Emergency Charge (₹)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.emergency_charge}
              onChange={(e) => field("emergency_charge", e.target.value)}
              placeholder="e.g. 1500.00"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.emergency_charge && <p className="text-red-500 text-xs mt-1">{errors.emergency_charge}</p>}
          </div>

          {/* Commission Rate */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Commission Rate (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.commission_in_percentage}
              onChange={(e) => field("commission_in_percentage", e.target.value)}
              placeholder="e.g. 5.0"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {errors.commission_in_percentage && <p className="text-red-500 text-xs mt-1">{errors.commission_in_percentage}</p>}
          </div>

          {/* Payment Term */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Payment Term
            </label>
            <select
              value={form.payment_term}
              onChange={(e) => field("payment_term", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Select payment term</option>
              {PAYMENT_TERMS.map(({ label, value }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {errors.payment_term && <p className="text-red-500 text-xs mt-1">{errors.payment_term}</p>}
          </div>

          {/* Contract Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Contract Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.contract_start_at}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => field("contract_start_at", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {errors.contract_start_at && <p className="text-red-500 text-xs mt-1">{errors.contract_start_at}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Contract Expiry Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={form.contract_expiry_at}
                onChange={(e) => field("contract_expiry_at", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {errors.contract_expiry_at && <p className="text-red-500 text-xs mt-1">{errors.contract_expiry_at}</p>}
            </div>
          </div>

          {/* Auto Renewal */}
          <div className="flex items-center gap-2">
            <input
              id="auto_renewal"
              type="checkbox"
              checked={form.has_auto_renewal}
              onChange={(e) => field("has_auto_renewal", e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="auto_renewal" className="text-sm text-gray-700 cursor-pointer">
              Enable Auto Renewal
            </label>
          </div>

          {/* Diagnostic Test Pricing */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-xs font-medium text-gray-700">Diagnostic Test Pricing</label>
              <button
                type="button"
                onClick={addDiagRow}
                className="text-xs text-blue-600 hover:underline"
              >
                + Add Test
              </button>
            </div>
            <div className="space-y-2">
              {diagRows.map((row, i) => (
                <div key={i} className="flex flex-col gap-1">
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={row.key}
                      maxLength={MAX_DIAGNOSTIC_NAME_LEN}
                      onChange={(e) => { updateDiagRow(i, "key", e.target.value); setErrors((er) => ({ ...er, [`diag_${i}`]: "" })); }}
                      placeholder="Test name (e.g. blood_test)"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.value}
                      onChange={(e) => { updateDiagRow(i, "value", e.target.value); setErrors((er) => ({ ...er, [`diag_${i}`]: "" })); }}
                      placeholder="Price (₹)"
                      className={`w-28 border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors[`diag_${i}`] ? "border-red-400" : "border-gray-300"}`}
                    />
                    {diagRows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDiagRow(i)}
                        className="text-gray-400 hover:text-red-500 text-lg leading-none"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {errors[`diag_${i}`] && (
                    <p className="text-red-500 text-xs pl-1">{errors[`diag_${i}`]}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save Config"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Financial Config Tab ─────────────────────────────────────────────────────

const FinancialConfigTab = ({ hospitalId, currentRole, showModal, setShowModal }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState("");
  const fetchedForId = useRef(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFinancialConfig(currentRole, hospitalId);
      const data = res?.msg || res?.data || res;
      const cfg = data && typeof data === "object" && data.payment_term !== undefined ? data : null;
      setConfig(cfg);
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [currentRole, hospitalId]);

  useEffect(() => {
    if (fetchedForId.current === hospitalId) return;
    fetchedForId.current = hospitalId;
    fetchConfig();
  }, [hospitalId, fetchConfig]);

  const handleSave = async (payload) => {
    setSaveError("");
    try {
      if (config) {
        await editFinancialConfig(currentRole, hospitalId, payload);
      } else {
        await saveFinancialConfig(currentRole, hospitalId, payload);
      }
      setShowModal(false);
      await fetchConfig();
    } catch (err) {
      setSaveError(err.message || "Failed to save financial config.");
      throw err;
    }
  };

  const PAYMENT_TERM_LABEL = Object.fromEntries(PAYMENT_TERMS.map(({ label, value }) => [value, label]));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold text-gray-900">Financial Configuration</h3>
        <button
          onClick={() => { setSaveError(""); setShowModal(true); }}
          className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          {config ? "Edit Config" : "+ Set Config"}
        </button>
      </div>

      {saveError && (
        <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded-md">{saveError}</div>
      )}

      {loading && <p className="text-sm text-gray-500">Loading financial config...</p>}
      {!loading && !config && (
        <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-200 rounded-md">
          No financial config set yet. Click <strong>+ Set Config</strong> to get started.
        </div>
      )}

      {!loading && config && (
        <div className="space-y-4">
          {/* Fees & Commission */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Fees & Commission</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <Info label="Base Consult Fee" value={config.base_consult_fee ? `₹${config.base_consult_fee}` : "N/A"} />
              <Info label="Emergency Charge" value={config.emergency_charge ? `₹${config.emergency_charge}` : "N/A"} />
              <Info label="Commission Rate" value={config.commission_in_percentage != null ? `${config.commission_in_percentage}%` : "N/A"} />
            </div>
          </div>

          {/* Contract Terms */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Contract Terms</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Info label="Payment Term" value={PAYMENT_TERM_LABEL[config.payment_term] ?? config.payment_term ?? "N/A"} />
              <Info label="Contract Start" value={config.contract_start_at ?? "N/A"} />
              <Info label="Contract Expiry" value={config.contract_expiry_at ?? "N/A"} />
              <div>
                <div className="text-gray-500 text-xs">Auto Renewal</div>
                <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-md font-medium ${config.has_auto_renewal ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {config.has_auto_renewal ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
          </div>

          {/* Diagnostic Test Pricing */}
          {config.diagnostic_test_pricing && typeof config.diagnostic_test_pricing === "object" &&
            Object.keys(config.diagnostic_test_pricing).length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Diagnostic Test Pricing</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(config.diagnostic_test_pricing).map(([test, price]) => (
                  <div key={test} className="bg-white border border-gray-200 rounded-md px-3 py-2">
                    <div className="text-xs text-gray-500 capitalize">{test.replace(/_/g, " ")}</div>
                    <div className="text-sm font-medium text-gray-900">₹{price}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <FinancialConfigFormModal
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          existing={config}
        />
      )}
    </div>
  );
};


// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = ["Overview", "Contacts", "Documents", "Media & Management", "Bank Details", "Treatment Packages", "Financial Config"];

const HospitalDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hospitalId } = useParams();
  const currentRole = getCurrentRole();

  const [hospital, setHospital] = useState(location.state?.hospital || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("Overview");
  const [showContactModal, setShowContactModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showTreatmentPackageModal, setShowTreatmentPackageModal] = useState(false);
  const [showFinancialConfigModal, setShowFinancialConfigModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const fetchedForKey = useRef(null);

  useEffect(() => {
    if (!hospitalId) return;
    const key = `${hospitalId}|${currentRole}`;
    if (fetchedForKey.current === key) return;
    fetchedForKey.current = key;
    const fetchDetail = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getHospitalDetail(currentRole, hospitalId);
        const detail = response?.msg || response?.data || response;
        setHospital(detail);
      } catch (err) {
        setError(err.message || "Failed to fetch hospital detail.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [hospitalId, currentRole]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">

        {/* Back Button + Tabs row */}
        <div className="flex items-center gap-0">
          <button
            onClick={() => navigate(resolveListBackTarget(location.state, HOSPITAL_LIST_PATH, HOSPITAL_LIST_URL_KEY))}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600 mr-6 shrink-0"
          >
            ← Back to Hospitals
          </button>

          <div className="border-b border-gray-200 w-full">
            <nav className="flex gap-0" aria-label="Tabs">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {loading && <div className="text-sm text-gray-500">Loading details...</div>}
        {error && (
          <div className="text-sm text-red-700 bg-red-50 p-3 rounded-md">
            {error}
          </div>
        )}
        {!loading && !hospital && !error && (
          <div className="text-red-600 text-sm">
            No hospital data available. Please go back and select again.
          </div>
        )}

        {hospital && (
          <>
            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-md p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900">
                {hospital.name || "Hospital"}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {hospital.address_line}, {hospital.city}, {hospital.state}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="px-3 py-1 text-xs rounded-md bg-blue-100 text-blue-700">
                  {hospital.hospital_type || "N/A"}
                </span>
                {/* <span className="px-3 py-1 text-xs rounded-md bg-purple-100 text-purple-700">
                  {hospital.facility_type || "N/A"}
                </span> */}
              </div>
            </div>

            {/* Overview Tab */}
            {activeTab === "Overview" && (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="bg-white border rounded-md p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold">Basic Information</h3>
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      title="Edit hospital"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <Info label="Pincode" value={hospital.pincode} />
                    <Info label="Type" value={hospital.hospital_type} />
                    {/* <Info label="Facility" value={hospital.facility_type} /> */}
                    {hospital.hospital_type === "single_specialty" && (
                      <Info label="Specialty" value={hospital.specialty} />
                    )}
                    <Info
                      label="Accreditations"
                      value={
                        Array.isArray(hospital.accreditations)
                          ? hospital.accreditations.join(", ")
                          : hospital.accreditations
                      }
                    />
                  </div>
                </div>

                {/* Infrastructure */}
                <div className="bg-white border rounded-md p-6 shadow-sm">
                  <h3 className="text-base font-semibold mb-4">Infrastructure</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <Info label="Beds" value={hospital.no_of_bed} />
                    <Info label="Doctors" value={hospital.no_of_doctor} />
                    <Info label="ICUs" value={hospital.no_of_icu} />
                    <Info label="Wards" value={hospital.no_of_ward} />
                    <Info label="Operation Theatres" value={hospital.no_of_ot} />
                    <Info
                      label="OT Types"
                      value={
                        Array.isArray(hospital.ot_types)
                          ? hospital.ot_types.join(", ")
                          : hospital.ot_types
                      }
                    />
                  </div>
                </div>

                {/* Facilities */}
                <div className="bg-white border rounded-md p-6 shadow-sm">
                  <h3 className="text-base font-semibold mb-4">Facilities</h3>
                  <div className="flex flex-wrap gap-3">
                    {[
                      ["Pharmacy", hospital.has_pharmacy],
                      ["Radiology", hospital.has_radiology],
                      ["24x7 Emergency", hospital.has_emergency],
                      ["Blood Bank", hospital.has_blood_bank],
                    ]
                      .filter(([, active]) => active)
                      .map(([label]) => (
                        <Badge key={label} label={label} active />
                      ))}
                  </div>
                </div>

                {/* Specialties Offered */}
                {Array.isArray(hospital.specialties_offered) && hospital.specialties_offered.length > 0 && (
                  <div className="bg-white border rounded-md p-6 shadow-sm">
                    <h3 className="text-base font-semibold mb-4">Specialties Offered</h3>
                    <div className="flex flex-wrap gap-3">
                      {hospital.specialties_offered.map((s) => (
                        <span key={s} className="px-3 py-1 text-xs rounded-md font-medium bg-green-50 text-green-700">
                          {SPECIALTY_LABEL[s] ?? s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Amenities */}
                {Array.isArray(hospital.amenities) && hospital.amenities.length > 0 && (
                  <div className="bg-white border rounded-md p-6 shadow-sm">
                    <h3 className="text-base font-semibold mb-4">Amenities</h3>
                    <div className="flex flex-wrap gap-3">
                      {hospital.amenities.map((a) => (
                        <span key={a} className="px-3 py-1 text-xs rounded-md font-medium bg-blue-50 text-blue-700">
                          {AMENITY_LABEL[a] ?? a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Diagnostic Services */}
                {Array.isArray(hospital.diagnostic_services) && hospital.diagnostic_services.length > 0 && (
                  <div className="bg-white border rounded-md p-6 shadow-sm">
                    <h3 className="text-base font-semibold mb-4">Diagnostic Services</h3>
                    <div className="flex flex-wrap gap-3">
                      {hospital.diagnostic_services.map((d) => (
                        <span key={d} className="px-3 py-1 text-xs rounded-md font-medium bg-purple-50 text-purple-700">
                          {DIAGNOSTIC_SERVICES_LABEL[d] ?? d}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Location */}
                <div className="bg-white border rounded-md p-6 shadow-sm">
                  <h3 className="text-base font-semibold mb-4">Location Details</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {/* <Info label="Latitude" value={hospital.latitude} /> */}
                    {/* <Info label="Longitude" value={hospital.longitude} /> */}
                    <Info label="Bus Station Distance (km)" value={hospital.bus_stand_in_km} />
                    <Info label="Railway Distance (km)" value={hospital.railway_in_km} />
                    <Info label="Airport Distance (km)" value={hospital.airport_in_km} />
                  </div>
                </div>
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === "Contacts" && (
              <div className="bg-white border rounded-md p-6 shadow-sm">
                <ContactsTab
                  hospitalId={hospitalId}
                  currentRole={currentRole}
                  showModal={showContactModal}
                  setShowModal={setShowContactModal}
                />
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === "Documents" && (
              <div className="bg-white border rounded-md p-6 shadow-sm">
                <DocumentsTab
                  hospitalId={hospitalId}
                  currentRole={currentRole}
                  showModal={showDocumentModal}
                  setShowModal={setShowDocumentModal}
                />
              </div>
            )}

            {/* Media & Management Tab */}
            {activeTab === "Media & Management" && (
              <div className="bg-white border rounded-md p-6 shadow-sm">
                <MediaTab
                  hospitalId={hospitalId}
                  currentRole={currentRole}
                  showModal={showMediaModal}
                  setShowModal={setShowMediaModal}
                />
              </div>
            )}

            {/* Bank Details Tab */}
            {activeTab === "Bank Details" && (
              <div className="bg-white border rounded-md p-6 shadow-sm">
                <BankTab
                  hospitalId={hospitalId}
                  currentRole={currentRole}
                  showModal={showBankModal}
                  setShowModal={setShowBankModal}
                />
              </div>
            )}

            {/* Treatment Packages Tab */}
            {activeTab === "Treatment Packages" && (
              <div className="bg-white border rounded-md p-6 shadow-sm">
                <TreatmentPackagesTab
                  hospitalId={hospitalId}
                  currentRole={currentRole}
                  showModal={showTreatmentPackageModal}
                  setShowModal={setShowTreatmentPackageModal}
                />
              </div>
            )}

            {/* Financial Config Tab */}
            {activeTab === "Financial Config" && (
              <div className="bg-white border rounded-md p-6 shadow-sm">
                <FinancialConfigTab
                  hospitalId={hospitalId}
                  currentRole={currentRole}
                  showModal={showFinancialConfigModal}
                  setShowModal={setShowFinancialConfigModal}
                />
              </div>
            )}

            {showEditModal && (
              <HospitalFormModal
                hospital={hospital}
                currentRole={currentRole}
                onClose={() => setShowEditModal(false)}
                onSaved={() => {
                  setShowEditModal(false);
                  window.location.reload();
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HospitalDetail;
